(() => {
  if (customElements.get('fx-social-carousel')) return;

  class FxSocialCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.rail = this.querySelector('[data-social-rail]');
      this.cards = Array.from(this.querySelectorAll('[data-social-card]'));
      this.status = this.querySelector('[data-social-status]');
      if (!this.rail || !this.cards.length) return;

      this.currentIndex = 0;
      this.dragging = false;
      this.dragPointerId = null;
      this.startX = 0;
      this.startScrollLeft = 0;
      this.pendingScrollLeft = 0;
      this.scrollFrame = null;
      this.statusFrame = null;
      this.abortController = new AbortController();
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.bindEvents();
      this.updateStatus(0);
    }

    disconnectedCallback() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.statusFrame) cancelAnimationFrame(this.statusFrame);
      this.abortController?.abort();
      delete this.dataset.initialized;
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-social-previous]')) {
          this.scrollToCard(this.currentIndex - 1);
          return;
        }
        if (event.target.closest('[data-social-next]')) this.scrollToCard(this.currentIndex + 1);
      }, { signal });

      this.rail.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.scrollToCard(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal });

      this.rail.addEventListener('scroll', () => {
        if (this.statusFrame) return;
        this.statusFrame = requestAnimationFrame(() => {
          this.statusFrame = null;
          this.updateStatus(this.findNearestCard());
        });
      }, { passive: true, signal });

      this.rail.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || (event.pointerType !== 'mouse' && event.pointerType !== 'pen')) return;

        event.preventDefault();
        this.dragging = true;
        this.dragPointerId = event.pointerId;
        this.startX = event.clientX;
        this.startScrollLeft = this.rail.scrollLeft;
        this.pendingScrollLeft = this.startScrollLeft;
        this.rail.classList.add('is-dragging');
        this.rail.setPointerCapture(event.pointerId);
      }, { signal });

      this.rail.addEventListener('pointermove', (event) => {
        if (!this.dragging || event.pointerId !== this.dragPointerId) return;
        this.pendingScrollLeft = this.startScrollLeft - (event.clientX - this.startX);

        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.rail.scrollLeft = this.pendingScrollLeft;
        });
      }, { signal });

      const finishDrag = (event) => {
        if (!this.dragging || event.pointerId !== this.dragPointerId) return;
        if (this.scrollFrame) {
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = null;
          this.rail.scrollLeft = this.pendingScrollLeft;
        }

        this.dragging = false;
        this.rail.classList.remove('is-dragging');
        if (this.rail.hasPointerCapture(event.pointerId)) this.rail.releasePointerCapture(event.pointerId);
        this.dragPointerId = null;
        this.scrollToCard(this.findNearestCard());
      };

      this.rail.addEventListener('pointerup', finishDrag, { signal });
      this.rail.addEventListener('pointercancel', finishDrag, { signal });

      this.addEventListener('shopify:block:select', (event) => {
        const card = event.target.closest('[data-social-card]');
        const index = this.cards.indexOf(card);
        if (index >= 0) this.scrollToCard(index);
      }, { signal });
    }

    findNearestCard() {
      const railLeft = this.rail.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.cards.forEach((card, index) => {
        const distance = Math.abs(card.offsetLeft - railLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    scrollToCard(requestedIndex) {
      const index = Math.min(Math.max(requestedIndex, 0), this.cards.length - 1);
      const card = this.cards[index];
      if (!card) return;

      this.currentIndex = index;
      this.rail.scrollTo({
        left: card.offsetLeft,
        behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
      });
      this.updateStatus(index);
    }

    updateStatus(index) {
      this.currentIndex = index;
      if (!this.status) return;
      const current = String(index + 1).padStart(2, '0');
      const total = String(this.cards.length).padStart(2, '0');
      this.status.textContent = `${current} / ${total}`;
    }
  }

  customElements.define('fx-social-carousel', FxSocialCarousel);
})();
